import React, { useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type {
  ServiceCapabilities,
  ServiceCapabilityIntent,
  ServiceCapabilityItem,
  ServiceCapabilityMode,
} from '../../types/firestore';
import { Alert, Badge, Button } from '../common/UIComponents';
import {
  createEmptyServiceCapabilities,
  formatServiceCapabilityIntents,
  getAdvancedCapabilitiesForObjects,
  getObjectsForProviderCategory,
  getQualificationType,
  getRiskLevel,
  normalizeIntentList,
  providerCapabilityCategories,
  providerCapabilityCategoryById,
  providerCapabilityObjectById,
  providerAdvancedCapabilityById,
  toCanonicalServiceCapabilities,
  type ProviderAdvancedCapabilityOption,
  type ProviderCategoryOption,
  type ProviderObjectOption,
  validateServiceCapabilities,
} from '../../utils/serviceCapabilities';

type MissingSuggestionPayload = {
  type: string;
  description: string;
  wantsToOffer: boolean;
};

interface ServiceCapabilitiesSectionProps {
  value: ServiceCapabilities | null | undefined;
  disabled?: boolean;
  isSaving?: boolean;
  onBack: () => void;
  onSave: (nextValue: ServiceCapabilities) => Promise<void> | void;
  onSubmitMissingSuggestion: (payload: MissingSuggestionPayload) => Promise<void> | void;
}

type DraftStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type SuggestionType = 'domain' | 'object' | 'service' | 'client_phrase' | 'other';

interface CapabilityDraft {
  editingItemId?: string;
  categoryId: string;
  intents: ServiceCapabilityIntent[];
  mode: ServiceCapabilityMode;
  selectedObjectIds: string[];
  objectIntentOverrides: Record<string, ServiceCapabilityIntent[]>;
  excludedObjectIds: string[];
  advancedCapabilityIds: string[];
}

const INTENT_OPTIONS: Array<{
  id: ServiceCapabilityIntent;
  label: string;
  description: string;
}> = [
  {
    id: 'N',
    label: 'Naprawa',
    description: 'Gdy coś jest zepsute albo nie działa.',
  },
  {
    id: 'D',
    label: 'Diagnostyka',
    description: 'Gdy klient chce sprawdzić przyczynę problemu.',
  },
  {
    id: 'U',
    label: 'Usługa',
    description: 'Gdy klient chce konkretną czynność.',
  },
];

const SUGGESTION_TYPES: Array<{ id: SuggestionType; label: string }> = [
  { id: 'domain', label: 'Obszaru / branży' },
  { id: 'object', label: 'Sprzętu / rzeczy' },
  { id: 'service', label: 'Usługi' },
  { id: 'client_phrase', label: 'Frazy, którą wpisują klienci' },
  { id: 'other', label: 'Inne' },
];

const priorityLabel = (priority?: string): number => {
  if (priority === 'high') return 0;
  if (priority === 'medium') return 1;
  if (priority === 'low') return 2;
  return 3;
};

const emptyDraft = (): CapabilityDraft => ({
  categoryId: '',
  intents: [],
  mode: 'selectedObjects',
  selectedObjectIds: [],
  objectIntentOverrides: {},
  excludedObjectIds: [],
  advancedCapabilityIds: [],
});

const cloneCapabilities = (value: ServiceCapabilities | null | undefined): ServiceCapabilities =>
  value && Array.isArray(value.items)
    ? toCanonicalServiceCapabilities({
        updatedAt: value.updatedAt ?? null,
        items: value.items,
      })
    : createEmptyServiceCapabilities();

const draftFromItem = (item: ServiceCapabilityItem): CapabilityDraft => ({
  editingItemId: item.id,
  categoryId: item.categoryId,
  intents: normalizeIntentList(item.intents),
  mode: item.mode,
  selectedObjectIds: item.objectSelections.map((selection) => selection.objectId),
  objectIntentOverrides: item.objectSelections.reduce<Record<string, ServiceCapabilityIntent[]>>(
    (acc, selection) => {
      if (selection.intents) {
        acc[selection.objectId] = normalizeIntentList(selection.intents);
      }
      return acc;
    },
    {}
  ),
  excludedObjectIds: [...item.excludedObjectIds],
  advancedCapabilityIds: [...item.advancedCapabilityIds],
});

const toggleInList = <T extends string>(source: T[], value: T): T[] =>
  source.includes(value) ? source.filter((item) => item !== value) : [...source, value];

const deriveCategoryExamples = (categoryId: string): string => {
  const objects = getObjectsForProviderCategory(categoryId).slice(0, 3);
  if (objects.length > 0) {
    return objects.map((object) => object.pluralLabel || object.label).join(', ');
  }
  const category = providerCapabilityCategoryById.get(categoryId);
  return category?.aliases.slice(0, 3).join(', ') || 'Zakres usług';
};

const summarizeObjectAliases = (object: ProviderObjectOption): string =>
  object.aliases.slice(0, 3).join(', ');

const getItemObjects = (item: ServiceCapabilityItem): ProviderObjectOption[] =>
  item.objectSelections
    .map((selection) => providerCapabilityObjectById.get(selection.objectId))
    .filter((object): object is ProviderObjectOption => Boolean(object));

const getItemSummary = (item: ServiceCapabilityItem): string => {
  const category = providerCapabilityCategoryById.get(item.categoryId);
  const modeSummary =
    item.mode === 'categoryWide'
      ? 'cały obszar'
      : `${item.objectSelections.length} ${
          item.objectSelections.length === 1 ? 'rzecz' : 'rzeczy'
        }`;
  return `${category?.label || 'Zakres usług'} · ${formatServiceCapabilityIntents(
    item.intents
  )} · ${modeSummary}`;
};

const getItemDetail = (item: ServiceCapabilityItem): string => {
  if (item.mode === 'categoryWide') {
    const exclusions = item.excludedObjectIds
      .map(
        (id) =>
          providerCapabilityObjectById.get(id)?.pluralLabel ||
          providerCapabilityObjectById.get(id)?.label
      )
      .filter(Boolean);
    return exclusions.length > 0 ? `Bez: ${exclusions.slice(0, 3).join(', ')}` : 'Bez wykluczeń';
  }

  const objects = getItemObjects(item).map((object) => object.pluralLabel || object.label);
  if (objects.length === 0) return 'Konkretne rzeczy bez etykiet';
  const preview = objects.slice(0, 3).join(', ');
  return objects.length > 3 ? `${preview} +${objects.length - 3}` : preview;
};

const getCategoryRiskRecords = (
  category: ProviderCategoryOption,
  objects: ProviderObjectOption[],
  capabilities: ProviderAdvancedCapabilityOption[]
) => [category, ...objects, ...capabilities];

const composeCapabilityItem = (draft: CapabilityDraft): ServiceCapabilityItem | null => {
  const category = providerCapabilityCategoryById.get(draft.categoryId);
  if (!category) return null;

  const selectedObjects = draft.selectedObjectIds
    .map((id) => providerCapabilityObjectById.get(id))
    .filter((object): object is ProviderObjectOption => Boolean(object));
  const selectedCapabilities = draft.advancedCapabilityIds
    .map((id) => providerAdvancedCapabilityById.get(id))
    .filter((capability): capability is ProviderAdvancedCapabilityOption => Boolean(capability));
  const riskRecords = getCategoryRiskRecords(category, selectedObjects, selectedCapabilities);

  return {
    id:
      draft.editingItemId ||
      `service-capability-${draft.categoryId.replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}`,
    domainId: category.domainId,
    categoryId: category.id,
    mode: draft.mode,
    intents: normalizeIntentList(draft.intents),
    objectSelections:
      draft.mode === 'selectedObjects'
        ? draft.selectedObjectIds.map((objectId) => ({
            objectId,
            intents: normalizeIntentList(draft.objectIntentOverrides[objectId] || draft.intents),
          }))
        : [],
    excludedObjectIds: draft.mode === 'categoryWide' ? [...draft.excludedObjectIds] : [],
    advancedCapabilityIds: [...draft.advancedCapabilityIds],
    requiresQualification: riskRecords.some((record) => record.requiresQualification),
    qualificationType: getQualificationType(riskRecords),
    riskLevel: getRiskLevel(riskRecords),
  };
};

const buildNextCapabilities = (
  current: ServiceCapabilities,
  item: ServiceCapabilityItem
): ServiceCapabilities => {
  const items = item.id
    ? current.items.some((currentItem) => currentItem.id === item.id)
      ? current.items.map((currentItem) => (currentItem.id === item.id ? item : currentItem))
      : [...current.items, item]
    : [...current.items, item];

  return toCanonicalServiceCapabilities({
    updatedAt: current.updatedAt ?? null,
    items,
  });
};

const groupAdvancedByObject = (
  capabilities: ProviderAdvancedCapabilityOption[]
): Record<string, ProviderAdvancedCapabilityOption[]> =>
  capabilities.reduce<Record<string, ProviderAdvancedCapabilityOption[]>>((acc, capability) => {
    acc[capability.objectId] = acc[capability.objectId] || [];
    acc[capability.objectId].push(capability);
    return acc;
  }, {});

const SectionHeader: React.FC<{
  onBack: () => void;
  title?: string;
}> = ({ onBack, title = 'Zakres usług' }) => (
  <div className="flex items-center gap-3">
    <Button
      type="button"
      variant="neutral"
      size="sm"
      onClick={onBack}
      icon={<ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />}
      className="shrink-0"
    >
      Dane serwisu
    </Button>
    <h2 className="min-w-0 text-xl font-semibold text-primary-light-100">{title}</h2>
  </div>
);

const StepHeader: React.FC<{ step: DraftStep; title: string; copy?: string }> = ({
  step,
  title,
  copy,
}) => (
  <div className="space-y-1">
    <p className="text-xs font-semibold uppercase tracking-wide text-ratuj-300">Krok {step}/7</p>
    <h3 className="text-lg font-semibold text-primary-light-100">{title}</h3>
    {copy && <p className="text-sm text-primary-light-300">{copy}</p>}
  </div>
);

const IntentToggle: React.FC<{
  option: (typeof INTENT_OPTIONS)[number];
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}> = ({ option, checked, disabled, onChange }) => (
  <label
    className={`flex min-h-[74px] cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition-colors ${
      checked
        ? 'border-ratuj-300/75 bg-ratuj-500/15 text-primary-light-100'
        : 'border-primary-dark-300/70 bg-primary-dark-200/35 text-primary-light-300 hover:border-primary-dark-400'
    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
    />
    <span>
      <span className="block text-sm font-semibold">{option.label}</span>
      <span className="mt-1 block text-sm text-primary-light-300">{option.description}</span>
    </span>
  </label>
);

const ServiceCapabilitiesSection: React.FC<ServiceCapabilitiesSectionProps> = ({
  value,
  disabled = false,
  isSaving = false,
  onBack,
  onSave,
  onSubmitMissingSuggestion,
}) => {
  const current = useMemo(() => cloneCapabilities(value), [value]);
  const [draft, setDraft] = useState<CapabilityDraft | null>(null);
  const [step, setStep] = useState<DraftStep>(1);
  const [objectQuery, setObjectQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedAdvancedObjects, setExpandedAdvancedObjects] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [suggestionType, setSuggestionType] = useState<SuggestionType>('object');
  const [suggestionDescription, setSuggestionDescription] = useState('');
  const [suggestionWantsToOffer, setSuggestionWantsToOffer] = useState(false);
  const [suggestionSuccess, setSuggestionSuccess] = useState<string | null>(null);
  const [isSuggestionSending, setIsSuggestionSending] = useState(false);

  const selectedCategory = draft
    ? providerCapabilityCategoryById.get(draft.categoryId) || null
    : null;
  const categoryObjects = selectedCategory
    ? getObjectsForProviderCategory(selectedCategory.id)
    : [];
  const filteredObjects = categoryObjects.filter((object) => {
    const query = objectQuery.trim().toLowerCase();
    if (!query) return true;
    return [object.label, object.pluralLabel, ...object.aliases]
      .filter(Boolean)
      .some((valueToSearch) => String(valueToSearch).toLowerCase().includes(query));
  });
  const popularObjects = filteredObjects.filter((object) => object.priority === 'high').slice(0, 8);
  const remainingObjects = filteredObjects.filter((object) => !popularObjects.includes(object));
  const selectedObjects = draft
    ? draft.selectedObjectIds
        .map((id) => providerCapabilityObjectById.get(id))
        .filter((object): object is ProviderObjectOption => Boolean(object))
    : [];
  const advancedCapabilities = getAdvancedCapabilitiesForObjects(
    draft?.selectedObjectIds || []
  ).sort((a, b) => priorityLabel(a.priority) - priorityLabel(b.priority));
  const advancedByObject = groupAdvancedByObject(advancedCapabilities);
  const composedItem = draft ? composeCapabilityItem(draft) : null;

  const beginNewDraft = () => {
    setDraft(emptyDraft());
    setStep(1);
    setObjectQuery('');
    setShowAdvanced(false);
    setExpandedAdvancedObjects([]);
    setValidationError(null);
  };

  const editItem = (item: ServiceCapabilityItem) => {
    setDraft(draftFromItem(item));
    setStep(1);
    setObjectQuery('');
    setShowAdvanced(false);
    setExpandedAdvancedObjects([]);
    setValidationError(null);
  };

  const closeDraft = () => {
    setDraft(null);
    setValidationError(null);
    setSuggestionSuccess(null);
  };

  const updateDraft = (patch: Partial<CapabilityDraft>) => {
    setValidationError(null);
    setDraft((currentDraft) => (currentDraft ? { ...currentDraft, ...patch } : currentDraft));
  };

  const selectCategory = (category: ProviderCategoryOption) => {
    const defaultIntents = category.defaultIntents.length > 0 ? category.defaultIntents : ['D'];
    updateDraft({
      categoryId: category.id,
      intents: normalizeIntentList(defaultIntents),
      mode: 'selectedObjects',
      selectedObjectIds: [],
      objectIntentOverrides: {},
      excludedObjectIds: [],
      advancedCapabilityIds: [],
    });
    setStep(2);
  };

  const toggleDraftIntent = (intent: ServiceCapabilityIntent) => {
    if (!draft) return;
    const next = toggleInList(draft.intents, intent);
    if (next.length === 0) {
      setValidationError('Wybierz co najmniej jeden typ spraw.');
      return;
    }
    updateDraft({ intents: normalizeIntentList(next), objectIntentOverrides: {} });
  };

  const toggleObjectSelection = (objectId: string) => {
    if (!draft) return;
    const selectedObjectIds = toggleInList(draft.selectedObjectIds, objectId);
    const objectIntentOverrides = { ...draft.objectIntentOverrides };
    if (!selectedObjectIds.includes(objectId)) {
      delete objectIntentOverrides[objectId];
    }
    updateDraft({ selectedObjectIds, objectIntentOverrides });
  };

  const toggleObjectIntent = (objectId: string, intent: ServiceCapabilityIntent) => {
    if (!draft) return;
    const currentIntents = draft.objectIntentOverrides[objectId] || draft.intents;
    const nextIntents = toggleInList(currentIntents, intent);
    if (nextIntents.length === 0) {
      setValidationError('Typy dla wybranej rzeczy nie mogą być puste.');
      return;
    }
    updateDraft({
      objectIntentOverrides: {
        ...draft.objectIntentOverrides,
        [objectId]: normalizeIntentList(nextIntents),
      },
    });
  };

  const saveDraft = async () => {
    if (!draft || !composedItem) {
      setValidationError('Nie można zapisać pustego zakresu.');
      return;
    }
    if (draft.intents.length === 0) {
      setValidationError('Wybierz co najmniej jeden typ spraw.');
      setStep(2);
      return;
    }
    if (draft.mode === 'selectedObjects' && draft.selectedObjectIds.length === 0) {
      setValidationError('Wybierz co najmniej jedną rzecz.');
      setStep(4);
      return;
    }

    const nextCapabilities = buildNextCapabilities(current, composedItem);
    const error = validateServiceCapabilities(nextCapabilities);
    if (error) {
      setValidationError(error);
      return;
    }
    await onSave(nextCapabilities);
    closeDraft();
  };

  const removeItem = async (itemId: string) => {
    const items = current.items.filter((item) => item.id !== itemId);
    const nextCapabilities: ServiceCapabilities = toCanonicalServiceCapabilities({
      updatedAt: current.updatedAt ?? null,
      items,
    });
    await onSave(items.length > 0 ? nextCapabilities : createEmptyServiceCapabilities());
  };

  const submitSuggestion = async () => {
    const description = suggestionDescription.trim();
    if (!description) {
      setValidationError('Opisz krótko brakującą pozycję.');
      return;
    }
    setIsSuggestionSending(true);
    setValidationError(null);
    setSuggestionSuccess(null);
    try {
      await onSubmitMissingSuggestion({
        type: suggestionType,
        description,
        wantsToOffer: suggestionWantsToOffer,
      });
      setSuggestionDescription('');
      setSuggestionWantsToOffer(false);
      setSuggestionSuccess(
        'Dzięki. Sugestia trafi do moderacji. Na razie wybierz najbliższy istniejący obszar, żeby klienci mogli Cię znaleźć.'
      );
    } finally {
      setIsSuggestionSending(false);
    }
  };

  if (providerCapabilityCategories.length === 0) {
    return (
      <div className="space-y-4">
        <SectionHeader onBack={onBack} />
        <Alert variant="error">
          Nie udało się wczytać słownika Smart Search. Zakres usług nie może być edytowany bez
          publicznego słownika.
        </Alert>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="space-y-4">
        <SectionHeader onBack={onBack} />
        <div className="space-y-1">
          <p className="text-sm text-primary-light-300">
            Wybierz, co realnie przyjmujesz. System użyje tego do dopasowania klientów z
            wyszukiwarki.
          </p>
        </div>
        {validationError && <Alert variant="error">{validationError}</Alert>}
        <Button
          type="button"
          variant="primary"
          onClick={beginNewDraft}
          disabled={disabled || isSaving}
          icon={<PlusIcon className="h-4 w-4" aria-hidden="true" />}
          className="w-full sm:w-auto"
        >
          Dodaj zakres usług
        </Button>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-primary-light-400">
            Twoje zakresy
          </h3>
          {current.items.length === 0 ? (
            <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/35 p-4">
              <p className="font-semibold text-primary-light-100">Nie wybrano zakresu usług.</p>
              <p className="mt-1 text-sm text-primary-light-300">
                Dodaj pierwszy zakres, żeby klienci mogli Cię znaleźć.
              </p>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={beginNewDraft}
                disabled={disabled || isSaving}
                className="mt-3 w-full sm:w-auto"
              >
                Dodaj pierwszy zakres
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {current.items.map((item) => {
                const category = providerCapabilityCategoryById.get(item.categoryId);
                return (
                  <article
                    key={item.id}
                    className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/35 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-base font-semibold text-primary-light-100">
                          {category?.label || item.categoryId}
                        </h4>
                        <p className="mt-1 text-sm text-primary-light-300">
                          {getItemSummary(item)}
                        </p>
                        <p className="mt-1 text-sm text-primary-light-400">{getItemDetail(item)}</p>
                        {item.requiresQualification && (
                          <Badge variant="warning" size="sm" className="mt-2">
                            Wymaga kwalifikacji
                          </Badge>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => editItem(item)}
                          disabled={disabled || isSaving}
                          icon={<PencilSquareIcon className="h-4 w-4" aria-hidden="true" />}
                        >
                          Edytuj
                        </Button>
                        <Button
                          type="button"
                          variant="neutral"
                          size="sm"
                          onClick={() => {
                            void removeItem(item.id);
                          }}
                          disabled={disabled || isSaving}
                          icon={<TrashIcon className="h-4 w-4" aria-hidden="true" />}
                        >
                          <span className="sr-only">Usuń</span>
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  }

  const canGoNext =
    (step === 1 && Boolean(draft.categoryId)) ||
    (step === 2 && draft.intents.length > 0) ||
    step === 3 ||
    step === 4 ||
    step === 5 ||
    step === 6;

  return (
    <div className="space-y-4 pb-24">
      <SectionHeader onBack={closeDraft} title="Zakres usług" />
      {validationError && <Alert variant="error">{validationError}</Alert>}

      <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/35 p-3 sm:p-4">
        {step === 1 && (
          <div className="space-y-3">
            <StepHeader
              step={1}
              title="Wybierz obszar"
              copy="Pokazujemy tylko publiczne kategorie dostępne dla profili serwisów."
            />
            <div className="space-y-2">
              {providerCapabilityCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => selectCategory(category)}
                  disabled={disabled || isSaving}
                  className={`w-full rounded-lg border px-3 py-3 text-left transition-colors ${
                    draft.categoryId === category.id
                      ? 'border-ratuj-300/75 bg-ratuj-500/15'
                      : 'border-primary-dark-300/70 bg-primary-dark-100/45 hover:border-primary-dark-400'
                  }`}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-primary-light-100">
                        {category.label}
                      </span>
                      <span className="mt-1 block text-sm text-primary-light-300">
                        {deriveCategoryExamples(category.id)}
                      </span>
                    </span>
                    {category.requiresQualification && (
                      <Badge variant="warning" size="sm" className="shrink-0">
                        Wymaga kwalifikacji
                      </Badge>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <StepHeader
              step={2}
              title="Wybierz typy spraw"
              copy={selectedCategory ? selectedCategory.label : undefined}
            />
            <div className="grid gap-2 sm:grid-cols-3">
              {INTENT_OPTIONS.map((option) => (
                <IntentToggle
                  key={option.id}
                  option={option}
                  checked={draft.intents.includes(option.id)}
                  disabled={disabled || isSaving}
                  onChange={() => toggleDraftIntent(option.id)}
                />
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <StepHeader
              step={3}
              title="Wybierz dokładność zakresu"
              copy="Domyślnie zapisujemy konkretne rzeczy, ale możesz obsługiwać cały obszar."
            />
            {(['selectedObjects', 'categoryWide'] as ServiceCapabilityMode[]).map((mode) => (
              <label
                key={mode}
                className={`flex cursor-pointer gap-3 rounded-lg border px-3 py-3 ${
                  draft.mode === mode
                    ? 'border-ratuj-300/75 bg-ratuj-500/15'
                    : 'border-primary-dark-300/70 bg-primary-dark-100/45'
                }`}
              >
                <input
                  type="radio"
                  checked={draft.mode === mode}
                  disabled={disabled || isSaving}
                  onChange={() =>
                    updateDraft({
                      mode,
                      excludedObjectIds: mode === 'categoryWide' ? draft.excludedObjectIds : [],
                    })
                  }
                  className="mt-1 h-4 w-4 border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                />
                <span>
                  <span className="block text-sm font-semibold text-primary-light-100">
                    {mode === 'categoryWide' ? 'Obsługuję cały obszar' : 'Wybiorę konkretne rzeczy'}
                  </span>
                  <span className="mt-1 block text-sm text-primary-light-300">
                    {mode === 'categoryWide'
                      ? 'Będziesz widoczny przy aktualnych i przyszłych rzeczach w tej kategorii. Możesz dodać wykluczenia.'
                      : 'Będziesz widoczny tylko przy wybranych rzeczach.'}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        {step === 4 && selectedCategory && (
          <div className="space-y-3">
            <StepHeader
              step={4}
              title={
                draft.mode === 'categoryWide'
                  ? 'Dodaj wykluczenia'
                  : `Szukaj w ${selectedCategory.label}`
              }
              copy={
                draft.mode === 'categoryWide'
                  ? `${selectedCategory.label} · cały obszar · ${formatServiceCapabilityIntents(
                      draft.intents
                    )}`
                  : 'Wybierz rzeczy, które realnie przyjmujesz.'
              }
            />

            {draft.mode === 'categoryWide' ? (
              <div className="space-y-2">
                {categoryObjects.map((object) => (
                  <label
                    key={object.id}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={draft.excludedObjectIds.includes(object.id)}
                      disabled={disabled || isSaving || !object.excludeSelectable}
                      onChange={() =>
                        updateDraft({
                          excludedObjectIds: toggleInList(draft.excludedObjectIds, object.id),
                        })
                      }
                      className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-primary-light-100">
                        {object.pluralLabel || object.label}
                      </span>
                      {object.aliases.length > 0 && (
                        <span className="block text-sm text-primary-light-400">
                          {summarizeObjectAliases(object)}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <input
                  type="search"
                  value={objectQuery}
                  onChange={(event) => setObjectQuery(event.target.value)}
                  placeholder={`Szukaj w ${selectedCategory.label}...`}
                  className="block min-h-[42px] w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
                />

                {[
                  ['Popularne', popularObjects],
                  ['Pozostałe', remainingObjects],
                ].map(([label, objects]) => (
                  <div key={String(label)} className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                      {String(label)}
                    </h4>
                    {(objects as ProviderObjectOption[]).length === 0 ? (
                      <p className="text-sm text-primary-light-400">Brak pozycji w tej sekcji.</p>
                    ) : (
                      (objects as ProviderObjectOption[]).map((object) => {
                        const isSelected = draft.selectedObjectIds.includes(object.id);
                        const objectIntents =
                          draft.objectIntentOverrides[object.id] || draft.intents;
                        return (
                          <div
                            key={object.id}
                            className={`rounded-lg border px-3 py-2 ${
                              isSelected
                                ? 'border-ratuj-300/70 bg-ratuj-500/10'
                                : 'border-primary-dark-300/70 bg-primary-dark-100/45'
                            }`}
                          >
                            <label className="flex cursor-pointer items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={disabled || isSaving}
                                onChange={() => toggleObjectSelection(object.id)}
                                className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-primary-light-100">
                                  {object.pluralLabel || object.label}
                                </span>
                                {object.aliases.length > 0 && (
                                  <span className="mt-0.5 block text-sm text-primary-light-400">
                                    {summarizeObjectAliases(object)}
                                  </span>
                                )}
                              </span>
                            </label>
                            {isSelected && (
                              <div className="mt-3 border-t border-primary-dark-300/70 pt-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                                  Typy dla: {object.pluralLabel || object.label}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {INTENT_OPTIONS.map((option) => (
                                    <label
                                      key={option.id}
                                      className={`flex min-h-[34px] cursor-pointer items-center gap-2 rounded-lg border px-2.5 text-xs font-semibold ${
                                        objectIntents.includes(option.id)
                                          ? 'border-ratuj-300/70 bg-ratuj-500/15 text-primary-light-100'
                                          : 'border-primary-dark-300/70 bg-primary-dark-100/45 text-primary-light-300'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={objectIntents.includes(option.id)}
                                        disabled={disabled || isSaving}
                                        onChange={() => toggleObjectIntent(object.id, option.id)}
                                        className="h-3.5 w-3.5 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                                      />
                                      {option.id}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <StepHeader
              step={5}
              title="Mocne usługi"
              copy="Opcjonalnie zaznacz rzeczy, które szczególnie wykonujesz. Pomaga to lepiej dopasować klientów."
            />
            {!showAdvanced ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAdvanced(true)}
                disabled={disabled || isSaving || advancedCapabilities.length === 0}
                className="w-full sm:w-auto"
              >
                Pokaż mocne usługi
              </Button>
            ) : advancedCapabilities.length === 0 ? (
              <Alert variant="info">
                Mocne usługi pojawią się po wyborze konkretnych rzeczy. Nie pokazujemy pełnej listy
                opcji naraz.
              </Alert>
            ) : (
              <div className="space-y-3">
                {selectedObjects.map((object) => {
                  const objectCapabilities = advancedByObject[object.id] || [];
                  if (objectCapabilities.length === 0) return null;
                  const isExpanded = expandedAdvancedObjects.includes(object.id);
                  const visibleCapabilities = isExpanded
                    ? objectCapabilities
                    : objectCapabilities.slice(0, 7);
                  return (
                    <div
                      key={object.id}
                      className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 p-3"
                    >
                      <h4 className="text-sm font-semibold text-primary-light-100">
                        {object.pluralLabel || object.label}
                      </h4>
                      <div className="mt-2 space-y-2">
                        {visibleCapabilities.map((capability) => (
                          <label
                            key={capability.id}
                            className="flex cursor-pointer items-start gap-3 rounded-lg border border-primary-dark-300/70 bg-primary-dark-200/30 px-3 py-2"
                          >
                            <input
                              type="checkbox"
                              checked={draft.advancedCapabilityIds.includes(capability.id)}
                              disabled={disabled || isSaving}
                              onChange={() =>
                                updateDraft({
                                  advancedCapabilityIds: toggleInList(
                                    draft.advancedCapabilityIds,
                                    capability.id
                                  ),
                                })
                              }
                              className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                            />
                            <span className="text-sm text-primary-light-200">
                              {capability.profileLabel || capability.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      {objectCapabilities.length > 7 && (
                        <Button
                          type="button"
                          variant="neutral"
                          size="sm"
                          onClick={() =>
                            setExpandedAdvancedObjects((currentExpanded) =>
                              toggleInList(currentExpanded, object.id)
                            )
                          }
                          className="mt-2"
                          icon={<ChevronDownIcon className="h-4 w-4" aria-hidden="true" />}
                        >
                          {isExpanded ? 'Pokaż mniej' : 'Pokaż więcej'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-3">
            <StepHeader step={6} title="Brakuje pozycji?" />
            <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 p-3">
              <p className="text-sm font-semibold text-primary-light-100">
                Nie widzisz swojej usługi?
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {SUGGESTION_TYPES.map((type) => (
                  <label
                    key={type.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                      suggestionType === type.id
                        ? 'border-ratuj-300/70 bg-ratuj-500/15 text-primary-light-100'
                        : 'border-primary-dark-300/70 bg-primary-dark-200/35 text-primary-light-300'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={suggestionType === type.id}
                      onChange={() => setSuggestionType(type.id)}
                      className="h-4 w-4 border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                    />
                    {type.label}
                  </label>
                ))}
              </div>
              <textarea
                value={suggestionDescription}
                onChange={(event) => setSuggestionDescription(event.target.value)}
                rows={3}
                placeholder="Opisz krótko."
                className="mt-3 block w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400/60 shadow-sm focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70"
              />
              <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-primary-light-300">
                <input
                  type="checkbox"
                  checked={suggestionWantsToOffer}
                  onChange={(event) => setSuggestionWantsToOffer(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-400 focus:ring-ratuj-300"
                />
                Chcę oferować tę pozycję po zatwierdzeniu.
              </label>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={isSuggestionSending}
                onClick={() => {
                  void submitSuggestion();
                }}
                className="mt-3 w-full sm:w-auto"
              >
                Wyślij sugestię
              </Button>
              {suggestionSuccess && (
                <Alert variant="success" className="mt-3">
                  {suggestionSuccess}
                </Alert>
              )}
            </div>
          </div>
        )}

        {step === 7 && composedItem && selectedCategory && (
          <div className="space-y-3">
            <StepHeader
              step={7}
              title="Podsumowanie i zapis"
              copy="Sprawdź zakres przed zapisaniem."
            />
            <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/45 p-3">
              <h4 className="text-base font-semibold text-primary-light-100">
                {selectedCategory.label}
              </h4>
              <p className="mt-1 text-sm text-primary-light-300">
                Tryb: {draft.mode === 'categoryWide' ? 'cały obszar' : 'konkretne rzeczy'}
              </p>
              <p className="mt-1 text-sm text-primary-light-300">
                Typy domyślne: {formatServiceCapabilityIntents(draft.intents)}
              </p>
              {composedItem.requiresQualification && (
                <Alert variant="warning" className="mt-3">
                  Ten obszar może wymagać uprawnień. Dodaj informację o kwalifikacjach.
                  Administrator może ją zweryfikować.
                </Alert>
              )}
              {draft.mode === 'selectedObjects' && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                    Obsługujesz
                  </p>
                  {draft.selectedObjectIds.map((objectId) => {
                    const object = providerCapabilityObjectById.get(objectId);
                    const intents = draft.objectIntentOverrides[objectId] || draft.intents;
                    return (
                      <p key={objectId} className="text-sm text-primary-light-200">
                        {object?.pluralLabel || object?.label || objectId} · {intents.join(', ')}
                      </p>
                    );
                  })}
                </div>
              )}
              {draft.advancedCapabilityIds.length > 0 && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                    Mocne usługi
                  </p>
                  {draft.advancedCapabilityIds.map((capabilityId) => {
                    const capability = providerAdvancedCapabilityById.get(capabilityId);
                    return (
                      <p key={capabilityId} className="text-sm text-primary-light-200">
                        {capability?.profileLabel || capability?.label || capabilityId}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="fixed inset-x-0 bottom-0 z-20 border-t border-primary-dark-300/70 bg-primary-dark-600/95 px-3 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 shadow-2xl backdrop-blur sm:sticky sm:rounded-lg sm:border sm:bg-primary-dark-600/80 sm:p-3"
        data-product-feedback-trigger-obstruction="true"
      >
        <div className="mx-auto flex max-w-3xl gap-2">
          <Button
            type="button"
            variant="neutral"
            onClick={() => {
              if (step === 1) {
                closeDraft();
              } else {
                setStep((currentStep) => (currentStep - 1) as DraftStep);
              }
            }}
            disabled={isSaving}
            className="min-w-[96px]"
          >
            Wstecz
          </Button>
          {step < 7 ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                if (!canGoNext) {
                  setValidationError('Uzupełnij bieżący krok.');
                  return;
                }
                setValidationError(null);
                setStep((currentStep) => (currentStep + 1) as DraftStep);
              }}
              disabled={disabled || isSaving}
              className="flex-1"
              icon={<CheckIcon className="h-4 w-4" aria-hidden="true" />}
            >
              Dalej
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              isLoading={isSaving}
              onClick={() => {
                void saveDraft();
              }}
              disabled={disabled || isSaving}
              className="flex-1"
            >
              Zapisz zakres
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCapabilitiesSection;
